"use client";

import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RichTextEditor } from "../ui/rich-text-editor";
import { X, Save, AlertCircle } from "lucide-react";

interface ControlOwnerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  existingOwner?: {
    id: string;
    name: string;
    designation: string;
    emailid: string;
    authorityCompetence: string | { html: string; plainText: string };
  };
}

interface ControlOwnerFormData {
  name: string;
  designation: string;
  emailid: string;
  authorityCompetence: string;
}

export default function ControlOwnerFormModal({
  isOpen,
  onClose,
  onSave,
  existingOwner,
}: ControlOwnerFormModalProps) {
  const [formData, setFormData] = useState<ControlOwnerFormData>({
    name: "",
    designation: "",
    emailid: "",
    authorityCompetence: "",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ControlOwnerFormData, string>>>({});
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  // Update form data when existingOwner changes (for editing) or modal opens
  useEffect(() => {
    if (isOpen) {
      if (existingOwner) {
        setFormData({
          name: existingOwner.name || "",
          designation: existingOwner.designation || "",
          emailid: existingOwner.emailid || "",
          authorityCompetence: typeof existingOwner.authorityCompetence === 'object' 
            ? existingOwner.authorityCompetence.html 
            : existingOwner.authorityCompetence || "",
        });
      } else {
        // Reset form when opening for new owner
        setFormData({
          name: "",
          designation: "",
          emailid: "",
          authorityCompetence: "",
        });
      }
      setErrors({});
      setSaveStatus({ type: null, message: '' });
    }
  }, [existingOwner, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ControlOwnerFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.designation.trim()) {
      newErrors.designation = "Designation is required";
    }

    if (!formData.authorityCompetence.trim()) {
      newErrors.authorityCompetence = "Authority & Competence of Personnel is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setSaveStatus({ type: null, message: '' });

    try {
      // Load existing control owners from SharePoint
      let existingData: any = { controlOwners: {} };
      
      if (window.sharePointAPI && typeof window.sharePointAPI.downloadControlOwnersJson === 'function') {
        try {
          const downloadResult = await window.sharePointAPI.downloadControlOwnersJson();

          if (downloadResult.success && downloadResult.data) {
            existingData = downloadResult.data;
          }
        } catch (error) {
          console.log('File does not exist yet, will create new one:', error);
        }
      }

      // Determine if we're creating new or updating existing
      const isEditing = !!existingOwner;
      let ownerId: string;

      if (isEditing) {
        // Use existing ID when editing
        ownerId = existingOwner.id;
      } else {
        // Generate next Control Owner ID (CO-1, CO-2, etc.) for new owner
        const existingIds = Object.keys(existingData.controlOwners || {});
        const nextIdNumber = existingIds.length > 0
          ? Math.max(...existingIds.map(id => {
              const match = id.match(/^CO-(\d+)$/);
              return match ? parseInt(match[1], 10) : 0;
            })) + 1
          : 1;
        ownerId = `CO-${nextIdNumber}`;
      }

      // Extract plain text from HTML for authorityCompetence
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = formData.authorityCompetence;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';

      // Add or update control owner
      if (!existingData.controlOwners) {
        existingData.controlOwners = {};
      }

      existingData.controlOwners[ownerId] = {
        name: formData.name.trim(),
        designation: formData.designation.trim(),
        emailid: formData.emailid.trim() || "",
        authorityCompetence: {
          html: formData.authorityCompetence,
          plainText: plainText
        }
      };

      // Save to SharePoint
      if (window.sharePointAPI && typeof window.sharePointAPI.uploadControlOwnersJson === 'function') {
        const uploadResult = await window.sharePointAPI.uploadControlOwnersJson(existingData);

        if (uploadResult.success) {
          setSaveStatus({
            type: 'success',
            message: isEditing ? 'Control Owner updated successfully!' : 'Control Owner saved successfully!'
          });
          
          // Reset form only if creating new (not editing)
          if (!isEditing) {
            setFormData({
              name: "",
              designation: "",
              emailid: "",
              authorityCompetence: "",
            });
          }
          setErrors({});

          // Call onSave callback to refresh the list
          setTimeout(() => {
            onSave();
            onClose();
          }, 1500);
        } else {
          throw new Error(uploadResult.error || 'Failed to save control owner');
        }
      } else {
        throw new Error('SharePoint API not available');
      }
    } catch (error) {
      console.error('Save error:', error);
      setSaveStatus({
        type: 'error',
        message: `Failed to save control owner: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-2xl border-white/10 bg-black/90 text-white">
        <CardHeader className="flex flex-row items-center justify-between border-b border-white/10">
          <CardTitle className="text-xl font-semibold text-white">
            {existingOwner ? 'Edit Control Owner' : 'Add Control Owner'}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-white/60 hover:text-white"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {saveStatus.type && (
            <div className={`flex items-center gap-2 rounded-lg p-3 text-sm ${
              saveStatus.type === 'success'
                ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                : 'bg-red-500/10 text-red-400 border border-red-500/20'
            }`}>
              <AlertCircle className="h-4 w-4" />
              {saveStatus.message}
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-white">
                Name <span className="text-red-400">*</span>
              </Label>
              <Input
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (errors.name) setErrors({ ...errors, name: undefined });
                }}
                className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
                placeholder="Enter name"
              />
              {errors.name && (
                <p className="text-xs text-red-400">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-white">
                Designation <span className="text-red-400">*</span>
              </Label>
              <Input
                value={formData.designation}
                onChange={(e) => {
                  setFormData({ ...formData, designation: e.target.value });
                  if (errors.designation) setErrors({ ...errors, designation: undefined });
                }}
                className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
                placeholder="Enter designation"
              />
              {errors.designation && (
                <p className="text-xs text-red-400">{errors.designation}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-white">
                Email-ID
              </Label>
              <Input
                type="email"
                value={formData.emailid}
                onChange={(e) => {
                  setFormData({ ...formData, emailid: e.target.value });
                }}
                className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
                placeholder="Enter email ID (optional)"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-white">
                Authority & Competence of Personnel <span className="text-red-400">*</span>
              </Label>
              <RichTextEditor
                key={existingOwner?.id || 'new-owner'}
                content={formData.authorityCompetence}
                onChange={(content) => {
                  setFormData({ ...formData, authorityCompetence: content });
                  if (errors.authorityCompetence) setErrors({ ...errors, authorityCompetence: undefined });
                }}
                className="border-white/10 bg-white/5 text-white min-h-[200px]"
              />
              {errors.authorityCompetence && (
                <p className="text-xs text-red-400">{errors.authorityCompetence}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={saving}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#4da3ff] text-white hover:bg-[#4da3ff]/80"
            >
              {saving ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/20 border-t-white" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Control Owner
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

